import type { News } from "@boem/core/brief";
import type { Board, HistoryEntry, ItemKind, LinkDir, LinkKind, PinView, Stage, Viewer } from "@boem/core/shared";
import { apiBase } from "./org.ts";

export interface Me {
  state: "signed-out" | "not-on-team" | "member";
  viewer: Viewer | null;
  email: string | null;
  signIn: { microsoft: boolean; dev: boolean };
  /** the board is being updated (BOARD_READ_ONLY): changes are paused until then */
  readOnly?: { until: string } | null;
}

/** Fired on window when a change was refused because the board is read-only: detail = { until } (ISO) or null. */
export const READ_ONLY_EVENT = "board:read-only";

/** What the Claude consent page shows (GET /api/oauth/consent-info). */
export interface ConsentInfo {
  email: string;
  viewer: { name: string; role: string; isAdmin: boolean } | null;
  client: { name: string; verifiedHost: string | null; claudeCode: boolean; app: { app: string; verified: boolean } } | null;
  redirectHost: string | null;
  knownRedirect: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (res.status === 503 && data?.error === "read_only") {
    window.dispatchEvent(new CustomEvent(READ_ONLY_EVENT, { detail: (data.details as { until?: string } | undefined)?.until ?? null }));
  }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? "error", data?.message ?? `Request failed (${res.status})`, data?.details);
  return data as T;
}

/** The board's own calls go to this tab's board (org.ts): /api/... for OEMUAP, /api/orgs/<slug>/... for ?org=. */
export const api = {
  me: () => call<Me>("GET", "/api/me"),
  board: () => call<Board>("GET", `${apiBase()}/board`),
  archive: () => call<Board>("GET", `${apiBase()}/archive`),
  history: (num: number) => call<HistoryEntry[]>("GET", `${apiBase()}/items/${num}/history`),
  /** What's new: the meeting brief for a period (`days=3` or `since=workday|meeting|2026-10-01`, `by`, `project`) */
  whatsNew: (query: string) => call<News>("GET", `${apiBase()}/whatsnew${query ? `?${query}` : ""}`),
  createItem: (input: { title: string; body: string; priority: string; tags: string[]; role: string | null; kind?: ItemKind | null; parent?: number | null; due?: string | null; draft?: boolean }) =>
    call<{ num: number }>("POST", `${apiBase()}/items`, input),
  /** projects and sub-items: what it is (kind), what it sits under (parent), a project's own board */
  organize: (num: number, p: { kind?: ItemKind | null; parent?: number | null; ownBoard?: boolean }) =>
    call<{ moves: { num: number; from: string | null; to: string | null; returned: boolean }[] }>("POST", `${apiBase()}/items/${num}/organize`, p),
  /** admins: lock an item to its project so it can't be moved to another */
  setLocked: (num: number, value: boolean) => call("POST", `${apiBase()}/items/${num}/lock`, { value }),
  setStatus: (num: number, status: "open" | "closed", note?: string) => call("POST", `${apiBase()}/items/${num}/status`, { status, note }),
  /** the fix flow: developed -> deployed -> verified (which archives it), or back to To do */
  /** doneBy: who did the steps nobody marked (a lead or a verifier moving it on anyway) */
  setStage: (num: number, stage: Stage, note?: string, doneBy?: string) => call<{ archived: boolean }>("POST", `${apiBase()}/items/${num}/stage`, { stage, note, doneBy }),
  setArchived: (num: number, value: boolean) => call("POST", `${apiBase()}/items/${num}/archive`, { value }),
  archiveClosed: () => call<{ count: number }>("POST", `${apiBase()}/archive/closed`, {}),
  react: (num: number, kind: "agree" | "question" | null) => call("POST", `${apiBase()}/items/${num}/reaction`, { kind }),
  claim: (num: number) => call("POST", `${apiBase()}/items/${num}/claim`, {}),
  unclaim: (num: number) => call("DELETE", `${apiBase()}/items/${num}/claim`),
  assign: (num: number, memberId: string | null, note?: string) => call("POST", `${apiBase()}/items/${num}/assign`, { memberId, note }),
  // several people (OEM-285): the whole list (set), or some to put on (add) or take off (remove)
  assignPeople: (num: number, people: string[], mode: "set" | "add" | "remove" = "set", note?: string) =>
    call<{ ok: true; changed: boolean; people: string[] }>("POST", `${apiBase()}/items/${num}/assign`, { people, mode, note }),
  teamsResolved: (num: number, value: boolean) => call("POST", `${apiBase()}/items/${num}/teams-resolved`, { value }),
  nondev: (num: number, value: boolean) => call("POST", `${apiBase()}/items/${num}/nondev`, { value }),
  addNote: (num: number, body: string) => call<{ id: string }>("POST", `${apiBase()}/items/${num}/notes`, { body }),
  /** link this item to another (`to`: 188, "#188" or "01-07"); dir "in" turns it round: `to` blocks / duplicates this one */
  addLink: (num: number, link: { to: number | string; kind: LinkKind; note?: string; dir?: LinkDir }) => call<{ id: string }>("POST", `${apiBase()}/items/${num}/links`, link),
  /** remove a link (whoever made it, leads, admins) */
  removeLink: (id: string) => call("DELETE", `${apiBase()}/links/${encodeURIComponent(id)}`),
  removeNote: (num: number, id: string) => call("DELETE", `${apiBase()}/items/${num}/notes/${id}`),
  /** your own note's new text (its author only) */
  editNote: (num: number, id: string, body: string) => call<{ changed: boolean }>("PATCH", `${apiBase()}/items/${num}/notes/${encodeURIComponent(id)}`, { body }),
  /** an item's title and description (whoever logged it, and leads) */
  editItem: (num: number, edit: { title?: string; body?: string }) => call<{ changed: ("title" | "body")[] }>("PATCH", `${apiBase()}/items/${num}`, edit),
  /** an item's due date ("2026-10-21"), or null to clear it (OEM-267) */
  setDue: (num: number, due: string | null) => call<{ changed: boolean; due: string | null }>("PUT", `${apiBase()}/items/${num}/due`, { due }),
  setDraft: (num: number, value: boolean) => call<{ changed: boolean }>("PUT", `${apiBase()}/items/${num}/draft`, { value }),
  // -- pins: the signed-in person's pinned panels; every call answers with the pins as they are now
  pins: () => call<PinView[]>("GET", `${apiBase()}/pins`),
  /** pin a project's board or an item's card (kind left out: whichever the item is); pinning again changes nothing */
  pin: (num: number, kind?: PinView["kind"]) => call<PinView[]>("PUT", `${apiBase()}/pins/${num}`, kind ? { kind } : {}),
  unpin: (num: number) => call<PinView[]>("DELETE", `${apiBase()}/pins/${num}`),
  /** item numbers, first to last; pins left out keep their order after these */
  reorderPins: (nums: number[]) => call<PinView[]>("PUT", `${apiBase()}/pins`, { order: nums }),
  devMembers: () => call<{ id: string; name: string; role: string; isAdmin: boolean }[]>("GET", "/api/dev/members"),
  // -- Claude connector: OAuth sign-in and consent pages (phase 3) -----------------
  consentInfo: (clientId: string, redirectUri: string) =>
    call<ConsentInfo>("GET", `/api/oauth/consent-info?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`),
  /** Accept or deny; answers where to send the browser next. */
  oauthConsent: (accept: boolean, oauthQuery: string, scope?: string) =>
    call<{ url?: string; redirect_uri?: string }>("POST", "/api/auth/oauth2/consent", { accept, oauth_query: oauthQuery, ...(scope ? { scope } : {}) }),
  /** Microsoft sign-in that returns into the OAuth flow afterwards. */
  oauthSignInMicrosoft: (oauthQuery: string) =>
    call<{ url?: string }>("POST", "/api/auth/sign-in/social", { provider: "microsoft", callbackURL: "/", oauth_query: oauthQuery }),
  /** Development only (DEV_LOGIN): a real board session for a member, continuing the OAuth flow. */
  oauthDevSignIn: (memberId: string, oauthQuery: string) => call<{ url?: string }>("POST", "/api/auth/dev/sign-in", { memberId, oauth_query: oauthQuery }),
  devLogin: (memberId: string) => call("POST", "/api/dev/login", { memberId }),
  async signOut() {
    await call("POST", "/api/dev/logout", {}).catch(() => {});
    await call("POST", "/api/auth/sign-out", {}).catch(() => {});
  },
  /** Starts the Microsoft sign-in redirect. */
  async signInMicrosoft() {
    // come back to the same item if the sign-in started from a /#item-N link
    const back = /^#item-\d+$/.test(window.location.hash) ? `/${window.location.hash}` : "/";
    const r = await call<{ url?: string }>("POST", "/api/auth/sign-in/social", { provider: "microsoft", callbackURL: back });
    if (r?.url) window.location.href = r.url;
  },
};
